/// <reference path="./analytics.d.ts" />
/**
 * Lightweight analytics event layer.
 *
 * Event naming schema:
 *   category_action  (snake_case, max 40 chars)
 *
 * Categories: quote, contact, cta, call, email
 * Actions:    submit, click, start, complete
 *
 * Examples:
 *   quote_submit        — quote form submitted
 *   quote_step_complete — a quote wizard step completed
 *   cta_click           — primary CTA button clicked
 *   call_click          — click-to-call link clicked
 *   email_click         — mailto link clicked
 *   contact_submit      — contact form submitted
 *
 * Events fire exactly once per call; deduplication is the caller's
 * responsibility (e.g. disable button after submit).
 *
 * In debug / development the events are logged to console.debug.
 * A CustomEvent "prusa_analytics" is dispatched on `window` so any
 * external provider (GA4, Plausible, PostHog) can listen.
 */

export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, string | number | boolean>;
  timestamp: string;
}

const EVENT_LOG_KEY = "prusa_events";
const MAX_LOG = 50;

function persistEvent(event: AnalyticsEvent) {
  try {
    const stored = sessionStorage.getItem(EVENT_LOG_KEY);
    const events: AnalyticsEvent[] = stored ? JSON.parse(stored) : [];
    events.push(event);
    sessionStorage.setItem(EVENT_LOG_KEY, JSON.stringify(events.slice(-MAX_LOG)));
  } catch {
    // silent
  }
}

/**
 * Fire an analytics event.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string | number | boolean>,
): void {
  const event: AnalyticsEvent = {
    name,
    properties: {
      page: window.location.pathname,
      ...properties,
    },
    timestamp: new Date().toISOString(),
  };

  // 1. Console debug (visible in DevTools → Console when filtering "debug")
  console.debug("[Analytics]", event.name, event.properties);

  // 2. Persist to sessionStorage for debug view
  persistEvent(event);

  // 3. CustomEvent so external scripts can subscribe
  window.dispatchEvent(
    new CustomEvent("prusa_analytics", { detail: event }),
  );

  // 4. Forward to Google Analytics 4 if loaded
  if (typeof window.gtag === "function") {
    window.gtag("event", name, properties);
  }
}

/**
 * Read all events from the current session (for debug panel).
 */
export function getEventLog(): AnalyticsEvent[] {
  try {
    const stored = sessionStorage.getItem(EVENT_LOG_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}
